// Policy API Type Definitions
// Matches the new API response structure for dashboard and tenant policy endpoints

import type { DeploymentStatus } from '../../services/types';
export type { DeploymentStatus };

// ========== Common Types ==========

export type PolicyType = 'DlpCompliancePolicy' | 'AutoSensitivityLabelPolicy';
export type PolicyTabType = 'DLP' | 'AutoLabel'; // UI-only type for tabs
export type RuleStatus = 'Enabled' | 'Disabled';

// SIT/Label reference (from API response)
export interface SitReference {
  id: string;
  name: string | null;
  isDeployed?: boolean;
}

export interface LabelReference {
  id?: string;
  name?: string | null;
  displayName?: string | null;
  isDeployed?: boolean;
}

// ========== Dashboard View Types ==========

// Rule within a policy (dashboard view)
export interface DashboardPolicyRule {
  ruleId: string;
  ruleName: string;
  description: string | null;
  priority: number;
  status: RuleStatus;
  sits: SitReference[];  // API uses 'sits' (plural, lowercase)
  labels: LabelReference[];
  isDeployed?: boolean;
}

// Rule status per tenant
export interface TenantRuleStatus {
  ruleId: string;
  ruleName: string;
  isDeployed: boolean;
}

// Entity deployment status (SIT or Label per tenant)
export interface TenantEntityStatus {
  entityId: string;
  entityName: string;
  isDeployed: boolean;
}

// Tenant deployment info
export interface TenantDeployment {
  tenantId: string;
  tenantName: string;
  status: DeploymentStatus;
  deployedRulesCount: number;
  totalRulesCount: number;
  lastDeployed: string | null;
  ruleStatuses: TenantRuleStatus[];
  sitStatuses?: TenantEntityStatus[];
  labelStatuses?: TenantEntityStatus[];
}

// Policy in dashboard view
export interface DashboardPolicy {
  policyId: string;
  policyName: string;
  description: string | null;
  policyType: PolicyType;
  priority: number;
  lastModified: string;
  tenantCoverage: string; // e.g., "1/2"
  deployedTenantsCount: number;
  totalTenantsCount: number;
  rules: DashboardPolicyRule[];
  tenantDeployments: TenantDeployment[];
}

// Dashboard policies API response
export interface DashboardPoliciesResponse {
  baseTenantId: string;
  baseTenantName: string;
  totalAuthorizedTenants: number;
  policies: DashboardPolicy[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  /** Total DLP policies matching the search term (independent of policyType filter) — drives tab badge */
  dlpTotalCount?: number;
  /** Total AutoLabel policies matching the search term (independent of policyType filter) — drives tab badge */
  autoLabelTotalCount?: number;
}

// ========== Tenant View Types ==========

// Rule in tenant view (base rules)
export interface TenantBaseRule {
  ruleId: string;
  ruleName: string;
  description: string | null;
  priority: number;
  status: RuleStatus;
  sits: SitReference[];  // API uses 'sits' (plural, lowercase)
  labels: LabelReference[];  // API uses 'labels' (plural)
  isDeployed: boolean;
}

// Rule in tenant view (target rules)
export interface TenantTargetRule {
  ruleId: string;
  ruleName: string;
  description: string | null;
  priority: number;
  status: RuleStatus;
  sits: SitReference[];
  labels: LabelReference[];
  isDeployed?: boolean;
}

// Policy in tenant view
export interface TenantPolicy {
  policyId: string;
  policyName: string;
  description: string | null;
  policyType: PolicyType;
  priority: number;
  lastModified: string;
  status: DeploymentStatus;
  baseRules: TenantBaseRule[];
  targetRules: TenantTargetRule[];
  deployedRulesCount: number;
  totalBaseRulesCount: number;
}

// Tenant policies API response
export interface TenantPoliciesResponse {
  baseTenantId: string;
  baseTenantName: string;
  targetTenantId: string;
  targetTenantName: string;
  policies: TenantPolicy[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  /** Total DLP policies matching the search term (independent of policyType filter) — drives tab badge */
  dlpTotalCount?: number;
  /** Total AutoLabel policies matching the search term (independent of policyType filter) — drives tab badge */
  autoLabelTotalCount?: number;
}

// ========== Helper Types for UI ==========

// Extracted SIT/Label names for display
export interface ExtractedEntity {
  id: string;
  name: string; // Never null - filtered out if null
}

// UI-friendly policy data with extracted names
export interface UIPolicyRule {
  id: string;
  name: string;
  description: string;
  priority: number;
  status: RuleStatus;
  sitNames: string[];
  labelNames: string[];
}

// Priority mapping (for backward compatibility, but we'll display as number)
export type PolicyPriority = 'High' | 'Medium' | 'Low';

// Helper function to extract names from SIT/Label references
export function extractSitNames(sits: SitReference[] | undefined): string[] {
  if (!sits) return [];
  return sits
    .filter(sit => sit.name !== null && sit.name !== undefined)
    .map(sit => sit.name!);
}

export function extractLabelNames(labels: LabelReference[] | undefined): string[] {
  if (!labels) return [];
  return labels
    .filter(label => label.displayName != null || label.name != null)
    .map(label => label.displayName || label.name!);
}

/**
 * Key used to decide whether a SIT/Label already exists in a target tenant.
 *
 * This MUST stay aligned with the backend, which keys existence on the entity's
 * internal `name` (PolicyService.BuildEntityDeploymentStatuses groups on Name, and
 * check_entities_exist.kql matches metadata.name/metadata.Name). Matching on
 * `displayName` instead is wrong twice over:
 *   1. A label whose displayName differs from its name never matches, so it is
 *      reported as "will be created" forever even though it is deployed.
 *   2. Only displayName carries the tenant prefix (the internal name is never
 *      prefixed), so a prefixed label would stop matching its own base label.
 *
 * Lower-cased and trimmed because the backend compares with
 * StringComparer.OrdinalIgnoreCase and the KQL lower-cases both sides, whereas a
 * JS Set is case-sensitive — without this, casing drift alone breaks the match.
 */
export function entityMatchKey(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/**
 * Match key for a label. Uses the internal `name`, which is what the backend keys on.
 *
 * When `name` is missing we deliberately do NOT fall back to a bare `displayName` key:
 * displayName is not unique (three distinct labels in PPE share "COI Internal-Only") and
 * it carries the tenant prefix, so comparing it against the backend's internal names can
 * mark a *different* label as already existing. That failure is silent and harmful — the
 * label gets omitted from "will be created" and never gets deployed.
 *
 * Instead we namespace the fallback so it cannot collide with any internal name. The label
 * therefore sorts into "will be created" (the safe direction — at worst a redundant deploy,
 * which the engine treats as a no-op) and stays visible in the summary rather than silently
 * disappearing. Two entries that genuinely lack a name still match each other, so the
 * existing/new partition stays consistent on both sides.
 */
export function labelMatchKey(label: LabelReference): string {
  if (label.name != null && label.name.trim() !== '') {
    return entityMatchKey(label.name);
  }
  const display = entityMatchKey(label.displayName);
  return display ? `displayname-only:${display}` : '';
}

/** Human-readable text for a label: displayName preferred, internal name as fallback. */
export function labelDisplayText(label: LabelReference): string {
  return label.displayName || label.name || '';
}

// Helper to format description
export function formatDescription(description: string | null | undefined): string {
  return description || 'No description available';
}

export type RulePriority = 'High' | 'Medium' | 'Low';

export type PolicyRule = {
  id: string;
  name: string;
  description: string;
  priority: RulePriority;
  status: RuleStatus;
  sitNames: string[];
  labelNames: string[];
};

export type CorpPolicy = {
  id: string;
  name: string;
  priority: PolicyPriority;
  type: PolicyType;
  rules: PolicyRule[];
  lastModifiedTime: string;
};

// ========== Deployment Summary Types (for PDF export) ==========

export interface DeploymentSummaryPolicy {
  name: string;
  rules: { name: string }[];
}

export interface DeploymentSummary {
  totalPolicies: number;
  policies: DeploymentSummaryPolicy[];
  existingSits: string[];
  existingLabels: string[];
  newSits: string[];
  newLabels: string[];
}

// Deployment API and tracking types live in services/types.ts (entity-agnostic).
// Re-export the ones callers previously imported from here for convenience.
export type {
  BffEntityType,
  BffDeploymentTarget,
  BffEntityDetail,
  BffDeploymentRequest,
  BffDeploymentItem,
  BffDeploymentResponse,
  BffDeploymentEntityItem,
  BffDeploymentRequestStatus,
  BffDeploymentStatusListResponse,
  DeploymentResult,
  ApiDeploymentStatus,
  TrackedDeployment,
} from '../../services/types';
export {
  TERMINAL_DEPLOYMENT_STATUSES,
  mapApiStatusToDeploymentStatus,
  DEPLOYMENT_TRACKING_STORAGE_KEY,
  DEPLOYMENT_POLL_INTERVAL_MS,
  DEPLOYMENT_POLL_MAX_DURATION_MS,
} from '../../services/types';
